import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Plus, ClipboardList, Trash2, Package, ArrowDown } from 'lucide-react';

const REASONS = [
    { value: 'production', label: 'Production' },
    { value: 'perte', label: 'Perte / Gaspillage' },
    { value: 'autre', label: 'Autre' }
];

const DailyOutputsPage = () => {
    const { api, hasPermission, user } = useAuth();
    const [outputs, setOutputs] = useState([]);
    const [products, setProducts] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const canCreate = hasPermission('create_daily_output');

    const [formData, setFormData] = useState({
        items: [],
        notes: ''
    });

    const [newItem, setNewItem] = useState({
        product_id: '',
        quantity: '',
        unit: 'g',
        reason: 'production'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [outputsRes, productsRes, sectorsRes] = await Promise.all([
                api().get('/daily-outputs/today'),
                api().get('/products'),
                api().get('/sectors')
            ]);
            setOutputs(outputsRes.data);
            setProducts(productsRes.data);
            setUnits(sectorsRes.data.units || ['kg', 'g', 'L', 'mL', 'piece']);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = () => {
        if (!newItem.product_id || !newItem.quantity) {
            toast.error('Selectionnez un produit et une quantite');
            return;
        }
        
        const product = products.find(p => p.id === newItem.product_id);
        if (!product) return;
        
        setFormData({
            ...formData,
            items: [
                ...formData.items,
                {
                    product_id: newItem.product_id,
                    product_name: product.name,
                    quantity: parseFloat(newItem.quantity),
                    unit: newItem.unit,
                    reason: newItem.reason
                }
            ]
        });
        
        setNewItem({ product_id: '', quantity: '', unit: 'g', reason: 'production' });
    };

    const handleRemoveItem = (index) => {
        setFormData({
            ...formData,
            items: formData.items.filter((_, i) => i !== index)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.items.length === 0) {
            toast.error('Ajoutez au moins un article');
            return;
        }
        
        try {
            await api().post('/daily-outputs', {
                items: formData.items.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit: item.unit,
                    reason: item.reason
                })),
                notes: formData.notes
            });
            
            toast.success('Sortie enregistree');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const resetForm = () => {
        setFormData({ items: [], notes: '' });
        setNewItem({ product_id: '', quantity: '', unit: 'g', reason: 'production' });
    };

    const getReasonLabel = (reason) => {
        const found = REASONS.find(r => r.value === reason);
        return found ? found.label : reason;
    };

    const getReasonColor = (reason) => {
        const colors = {
            'production': 'bg-blue-500',
            'perte': 'bg-red-500',
            'autre': 'bg-gray-500'
        };
        return colors[reason] || 'bg-gray-500';
    };

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="daily-outputs-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Sorties du jour
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Declarez les sorties de stock pour la production
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-accent hover:bg-accent/90" data-testid="add-output-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouvelle sortie
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-display">Declarer une sortie</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                {/* Add item form */}
                                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                                    <Label className="text-sm font-semibold">Ajouter un article</Label>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-xs">Produit</Label>
                                        <Select 
                                            value={newItem.product_id}
                                            onValueChange={(v) => setNewItem({...newItem, product_id: v})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Selectionner un produit..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {products.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        {p.name} ({p.stock_quantity} {p.unit})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <Label className="text-xs">Quantite</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                value={newItem.quantity}
                                                onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Unite</Label>
                                            <Select 
                                                value={newItem.unit}
                                                onValueChange={(v) => setNewItem({...newItem, unit: v})}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {units.map(u => (
                                                        <SelectItem key={u} value={u}>{u}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Motif</Label>
                                            <Select 
                                                value={newItem.reason}
                                                onValueChange={(v) => setNewItem({...newItem, reason: v})}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {REASONS.map(r => (
                                                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    
                                    <Button 
                                        type="button" 
                                        variant="outline"
                                        className="w-full"
                                        onClick={handleAddItem}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter a la liste
                                    </Button>
                                </div>

                                {/* Items list */}
                                {formData.items.length > 0 && (
                                    <div className="space-y-2">
                                        <Label className="text-sm font-semibold">Articles a sortir</Label>
                                        <div className="border rounded-lg divide-y">
                                            {formData.items.map((item, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-3">
                                                    <div>
                                                        <p className="font-medium text-sm">{item.product_name}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-sm text-muted-foreground">
                                                                {item.quantity} {item.unit}
                                                            </span>
                                                            <Badge 
                                                                variant="secondary" 
                                                                className={`text-xs text-white ${getReasonColor(item.reason)}`}
                                                            >
                                                                {getReasonLabel(item.reason)}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => handleRemoveItem(idx)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="notes">Notes (optionnel)</Label>
                                    <Input
                                        id="notes"
                                        value={formData.notes}
                                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        placeholder="Remarques..."
                                    />
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Annuler
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        className="bg-accent hover:bg-accent/90"
                                        disabled={formData.items.length === 0}
                                    >
                                        Enregistrer
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Today's outputs */}
            <div className="space-y-4">
                {outputs.length === 0 ? (
                    <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                            <ClipboardList className="w-12 h-12 text-muted-foreground/50 mb-3" />
                            <p className="text-muted-foreground">Aucune sortie enregistree aujourd'hui</p>
                            {canCreate && (
                                <Button 
                                    variant="outline" 
                                    className="mt-4"
                                    onClick={() => setDialogOpen(true)}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Declarer une sortie
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    outputs.map(output => (
                        <Card key={output.id}>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-base font-display">
                                            Sortie par {output.created_by_name}
                                        </CardTitle>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(output.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                            {' - '}{output.total_items} article(s)
                                        </p>
                                    </div>
                                    <ArrowDown className="w-5 h-5 text-red-500" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {output.items?.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                                            <div className="flex items-center gap-3">
                                                <Package className="w-4 h-4 text-muted-foreground" />
                                                <div>
                                                    <p className="font-medium text-sm">{item.product_name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {item.quantity} {item.unit}
                                                        {item.converted_quantity !== item.quantity && (
                                                            <span> ({item.converted_quantity} {item.product_unit})</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <Badge 
                                                variant="secondary" 
                                                className={`text-xs text-white ${getReasonColor(item.reason)}`}
                                            >
                                                {getReasonLabel(item.reason)}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                                {output.notes && (
                                    <p className="text-sm text-muted-foreground mt-3 pt-3 border-t">
                                        Note: {output.notes}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
};

export default DailyOutputsPage;
